import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH } from '../config';
import {
  MAX_DICE_SLOTS,
  STARTING_UNLOCKED_DICE_SLOTS,
  chooseSkillFromOffer,
  collectRewardAndAdvance,
  combatRerollAll,
  combatRerollSingle,
  combatRerollSkillOffers,
  createEncounter,
  createSeededRng,
  enterDefeatResults,
  enterRewardPhase,
  encounterProgressLabel,
  formatCombatLogPanel,
  getLockedDice,
  getSkillDefinition,
  getUnlockedDice,
  holdDie,
  isBossEncounter,
  absorbEncounterIntoPlayLog,
  resolveEnemyActions,
  resolvePlayerAttack,
  restoreSeededRng,
  selectCombo,
  selectTarget,
  skillChoiceNeedsDie,
  syncDungeonEncounter,
  type ComboId,
  type DungeonRunState,
  type EncounterState,
  type SeededRng,
} from '../../domain';
import {
  createSaveStore,
  updateSavedGameRun,
  type SaveStore,
} from '../../persistence/saveStore';
import {
  accumulateProvisionalClassXp,
  addProvisionalLoot,
} from '../../persistence/settlement';
import { describeLootItem, type GeneratedLootItem } from '../../domain/loot';
import { getItemDef, RARITY_DISPLAY_COLORS } from '../../data/equipment';
import { addGameIcon, addItemIcon, type IconId } from '../ui/icons';

const ENEMY_STATUS_META: Record<
  'bleed' | 'stun' | 'vulnerable',
  { label: string; icon: IconId; color: string; tint: number }
> = {
  bleed: {
    label: 'Bleed',
    icon: 'status.bleed',
    color: '#e07070',
    tint: 0xe07070,
  },
  stun: {
    label: 'Stun',
    icon: 'status.stun',
    color: '#e8c45c',
    tint: 0xe8c45c,
  },
  vulnerable: {
    label: 'Vulnerable',
    icon: 'status.vulnerable',
    color: '#d48a4a',
    tint: 0xd48a4a,
  },
};

export interface CombatSceneData {
  mode?: 'practice' | 'dungeon';
  unlockedDiceSlots?: number;
  seed?: number;
  enemyTemplateIds?: string[];
  dungeonRun?: DungeonRunState;
  rngState?: number;
}

type UiButton = {
  bg: Phaser.GameObjects.Image;
  label: Phaser.GameObjects.Text;
  enabled: boolean;
  handler: () => void;
};

/**
 * Combat: temporary XP, skill offers, equipped skills under dice,
 * left→right skill activation feedback. Layout: enemies top · hero mid · dice bottom.
 * Dungeon mode wraps the encounter in a persistent mini-dungeon run.
 */
export class CombatScene extends Phaser.Scene {
  private state!: EncounterState;
  private rng!: SeededRng;
  private mode: 'practice' | 'dungeon' = 'practice';
  private dungeonRun: DungeonRunState | null = null;
  private saveStore: SaveStore = createSaveStore();
  private dieFaceTexts: Phaser.GameObjects.Text[] = [];
  private dieHoldHints: Phaser.GameObjects.Text[] = [];
  private dieSkillTexts: Phaser.GameObjects.Text[] = [];
  private dieImages: Phaser.GameObjects.Image[] = [];
  private statusText!: Phaser.GameObjects.Text;
  private comboText!: Phaser.GameObjects.Text;
  private playLogText!: Phaser.GameObjects.Text;
  private playLogScroll = 0;
  private lastLogLength = 0;
  private playLogOpen = false;
  private playLogDrawer?: Phaser.GameObjects.Container;
  private playLogToggleLabel?: Phaser.GameObjects.Text;
  /** Practice-mode run log (dungeon uses dungeonRun.playLog). */
  private practicePlayLog: string[] = [];
  private practicePlayLogCursor = 0;
  private xpText!: Phaser.GameObjects.Text;
  private progressText?: Phaser.GameObjects.Text;
  private hero!: Phaser.GameObjects.Image;
  private heroHpText!: Phaser.GameObjects.Text;
  private enemyImages = new Map<string, Phaser.GameObjects.Image>();
  private enemyLabels = new Map<string, Phaser.GameObjects.Text>();
  private enemyIntentLabels = new Map<string, Phaser.GameObjects.Text>();
  private enemyStatusRoots = new Map<string, Phaser.GameObjects.Container>();
  private buttons: UiButton[] = [];
  private endOverlay?: Phaser.GameObjects.Container;
  private rewardOverlay?: Phaser.GameObjects.Container;
  private levelUpOverlay?: Phaser.GameObjects.Container;
  private feedbackText!: Phaser.GameObjects.Text;
  private activationText!: Phaser.GameObjects.Text;
  /** When true, die taps toggle hold instead of spending a single reroll. */
  private holdMode = false;
  /** Blocks input while attack / enemy-turn animations play. */
  private actionLocked = false;
  /** Hold level-up / win / lose overlays until attack presentation finishes. */
  private holdResultOverlays = false;
  /** New skill waiting for the player to pick a target die. */
  private pendingSkillDieId: string | null = null;
  private trayTop = 0;
  private skillGlowTweens = new Map<number, Phaser.Tweens.Tween>();

  constructor() {
    super('Combat');
  }

  init(data: CombatSceneData): void {
    this.mode = data.mode ?? (data.dungeonRun ? 'dungeon' : 'practice');
    this.saveStore = createSaveStore();
    this.dungeonRun = null;

    if (this.mode === 'dungeon' && data.dungeonRun) {
      this.dungeonRun = {
        ...data.dungeonRun,
        playLog: data.dungeonRun.playLog ?? [...data.dungeonRun.encounter.log],
        playLogCursor:
          data.dungeonRun.playLogCursor ?? data.dungeonRun.encounter.log.length,
        equipmentSnapshot: data.dungeonRun.equipmentSnapshot ?? null,
      };
      this.rng = restoreSeededRng(data.rngState ?? data.dungeonRun.seed);
      // Save-compat: older runs may omit M9-A fields.
      const enc = this.dungeonRun.encounter;
      this.state = {
        ...enc,
        equipmentSnapshot:
          enc.equipmentSnapshot ?? this.dungeonRun.equipmentSnapshot ?? null,
        progress: {
          ...enc.progress,
          selectedEnhancementIds: enc.progress.selectedEnhancementIds ?? [],
        },
      };
      this.dungeonRun = { ...this.dungeonRun, encounter: this.state };
    } else {
      const unlocked = data.unlockedDiceSlots ?? STARTING_UNLOCKED_DICE_SLOTS;
      this.rng = createSeededRng(data.seed ?? 42);
      this.state = createEncounter(this.rng, {
        unlockedDiceSlots: unlocked,
        enemyTemplateIds: data.enemyTemplateIds,
      });
      this.practicePlayLog = [...this.state.log];
      this.practicePlayLogCursor = this.state.log.length;
    }

    this.dieFaceTexts = [];
    this.dieHoldHints = [];
    this.dieSkillTexts = [];
    this.dieImages = [];
    this.enemyImages.clear();
    this.enemyLabels.clear();
    this.enemyIntentLabels.clear();
    this.enemyStatusRoots.clear();
    this.buttons = [];
    this.holdMode = false;
    this.actionLocked = false;
    this.holdResultOverlays = false;
    this.pendingSkillDieId = null;
    this.endOverlay = undefined;
    this.rewardOverlay = undefined;
    this.levelUpOverlay = undefined;
    this.progressText = undefined;
    this.playLogScroll = 0;
    this.lastLogLength = 0;
    this.playLogOpen = false;
    this.playLogDrawer = undefined;
    this.playLogToggleLabel = undefined;
    for (const dieId of [...this.skillGlowTweens.keys()]) {
      this.clearSkillGlow(dieId);
    }
  }

  create(): void {
    // Extra top inset for iPhone notch; HTML also applies safe-area padding.
    const safeTop = 36;
    const safeBottom = 20;

    this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x0b1020).setOrigin(0);

    if (this.dungeonRun) {
      this.progressText = this.add
        .text(GAME_WIDTH / 2, safeTop - 4, encounterProgressLabel(this.dungeonRun), {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '12px',
          color: '#c9a227',
        })
        .setOrigin(0.5, 0);
    }

    this.add
      .text(GAME_WIDTH / 2, safeTop + 14, 'ENEMIES', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '11px',
        color: '#6e7694',
      })
      .setOrigin(0.5, 0);

    this.renderEnemies(safeTop + 36);

    if (this.dungeonRun?.phase === 'results') {
      this.scene.start('DungeonResult', { dungeonRun: this.dungeonRun });
      return;
    }

    const heroY = GAME_HEIGHT * 0.42;
    this.add
      .text(GAME_WIDTH / 2, heroY - 68, 'HERO', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '11px',
        color: '#6e7694',
      })
      .setOrigin(0.5);
    this.hero = this.add.image(GAME_WIDTH / 2, heroY, 'px-hero');
    this.heroHpText = this.add
      .text(GAME_WIDTH / 2, heroY + 54, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '13px',
        color: '#9ec1ff',
      })
      .setOrigin(0.5);
    this.xpText = this.add
      .text(GAME_WIDTH / 2, heroY + 72, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '11px',
        color: '#c9a227',
      })
      .setOrigin(0.5);

    this.tweens.add({
      targets: this.hero,
      scaleX: 1.04,
      scaleY: 0.96,
      duration: 700,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Compact bottom tray — play log is a toggle drawer, not always expanded.
    this.trayTop = GAME_HEIGHT - 268 - safeBottom;
    this.add
      .rectangle(GAME_WIDTH / 2, this.trayTop + 128, GAME_WIDTH - 12, 256, 0x161b2e)
      .setStrokeStyle(1, 0x2a3350);

    this.add.text(16, this.trayTop + 6, 'DICE', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '11px',
      color: '#6e7694',
    });

    this.comboText = this.add
      .text(GAME_WIDTH - 16, this.trayTop + 6, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '11px',
        color: '#e8d5a3',
        align: 'right',
      })
      .setOrigin(1, 0);

    this.renderDiceTray(this.trayTop + 46);
    this.renderControls(this.trayTop + 128);

    this.statusText = this.add
      .text(GAME_WIDTH / 2, this.trayTop + 196, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '11px',
        color: '#9aa3c0',
        align: 'center',
        wordWrap: { width: GAME_WIDTH - 28 },
      })
      .setOrigin(0.5);

    this.feedbackText = this.add
      .text(GAME_WIDTH / 2, heroY - 96, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        color: '#f2e6c8',
        align: 'center',
        wordWrap: { width: GAME_WIDTH - 40 },
      })
      .setOrigin(0.5)
      .setAlpha(0);

    this.activationText = this.add
      .text(GAME_WIDTH / 2, heroY + 90, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '12px',
        color: '#9ec1ff',
        align: 'center',
        wordWrap: { width: GAME_WIDTH - 28 },
      })
      .setOrigin(0.5);

    this.setupPlayLogDrawer();

    // Phosphor back affordance (textures preloaded in PreloadScene).
    // Keep combat mechanics untouched — navigation only.
    const backHit = this.add
      .rectangle(36, safeTop + 10, 88, 44, 0xffffff, 0.001)
      .setInteractive({ useHandCursor: true });
    addGameIcon(this, 'combat.back', 22, safeTop + 10, {
      size: 18,
      color: 0x9aa3c0,
    });
    this.add
      .text(36, safeTop + 10, 'Village', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        color: '#9aa3c0',
      })
      .setOrigin(0, 0.5);
    backHit.on('pointerup', () => {
      this.persistDungeon();
      // Resume-safe: do not abandon or settle on leave.
      this.scene.start(this.mode === 'practice' ? 'DebugMenu' : 'Village');
    });

    // Temporary developer control — unlock another die slot for multi-die skill tests.
    const dev = this.add
      .text(GAME_WIDTH - 16, safeTop, '+Die', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '13px',
        color: '#6e7694',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    dev.on('pointerup', () => this.onDevUnlockDie());

    this.refreshUi();
  }

  private setupPlayLogDrawer(): void {
    // Compact side toggle — closed by default so dice controls stay clear.
    const toggleY = this.trayTop - 18;
    const toggleBg = this.add
      .rectangle(GAME_WIDTH - 44, toggleY, 72, 28, 0x1c2438)
      .setStrokeStyle(1, 0xc9a227)
      .setInteractive({ useHandCursor: true })
      .setDepth(700);
    this.playLogToggleLabel = this.add
      .text(GAME_WIDTH - 44, toggleY, 'Log ▸', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '12px',
        color: '#e8d5a3',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(701);
    toggleBg.on('pointerup', () => this.togglePlayLog());

    const drawerH = 280;
    const drawerY = GAME_HEIGHT - drawerH - 8;
    const container = this.add.container(0, 0);
    const veil = this.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.35)
      .setOrigin(0)
      .setInteractive();
    veil.on('pointerup', () => this.setPlayLogOpen(false));

    const panel = this.add
      .rectangle(
        GAME_WIDTH / 2,
        drawerY + drawerH / 2,
        GAME_WIDTH - 12,
        drawerH,
        0x12182a,
        0.98,
      )
      .setStrokeStyle(1, 0xc9a227)
      .setInteractive();

    const title = this.add
      .text(16, drawerY + 10, 'PLAY LOG', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '12px',
        color: '#c9a227',
      })
      .setOrigin(0, 0);

    const close = this.add
      .text(GAME_WIDTH - 20, drawerY + 10, '✕', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#9aa3c0',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    close.on('pointerup', () => this.setPlayLogOpen(false));

    const older = this.add
      .text(GAME_WIDTH - 70, drawerY + 10, '▲', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        color: '#9aa3c0',
      })
      .setInteractive({ useHandCursor: true });
    const newer = this.add
      .text(GAME_WIDTH - 48, drawerY + 10, '▼', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        color: '#9aa3c0',
      })
      .setInteractive({ useHandCursor: true });
    older.on('pointerup', () => {
      this.playLogScroll += 1;
      this.refreshPlayLog();
    });
    newer.on('pointerup', () => {
      this.playLogScroll = Math.max(0, this.playLogScroll - 1);
      this.refreshPlayLog();
    });

    this.playLogText = this.add
      .text(14, drawerY + 34, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '11px',
        color: '#c5cce0',
        align: 'left',
        lineSpacing: 4,
        wordWrap: { width: GAME_WIDTH - 28 },
      })
      .setOrigin(0, 0);

    const hint = this.add
      .text(
        GAME_WIDTH / 2,
        drawerY + drawerH - 14,
        'Run history · capped · survives save',
        {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '10px',
          color: '#4a5168',
        },
      )
      .setOrigin(0.5);

    container.add([
      veil,
      panel,
      title,
      close,
      older,
      newer,
      this.playLogText,
      hint,
    ]);
    container.setDepth(850);
    container.setVisible(false);
    this.playLogDrawer = container;
  }

  private togglePlayLog(): void {
    this.setPlayLogOpen(!this.playLogOpen);
  }

  private setPlayLogOpen(open: boolean): void {
    this.playLogOpen = open;
    this.playLogDrawer?.setVisible(open);
    this.playLogToggleLabel?.setText(open ? 'Log ▾' : 'Log ▸');
    if (open) {
      this.playLogScroll = 0;
      this.refreshPlayLog();
    }
  }

  private activePlayLog(): string[] {
    if (this.dungeonRun) {
      return this.dungeonRun.playLog ?? [];
    }
    return this.practicePlayLog;
  }

  private absorbPlayLogFromEncounter(): void {
    if (this.dungeonRun) {
      this.dungeonRun = syncDungeonEncounter(this.dungeonRun, this.state);
      return;
    }
    const absorbed = absorbEncounterIntoPlayLog(
      this.practicePlayLog,
      this.practicePlayLogCursor,
      this.state.log,
    );
    this.practicePlayLog = absorbed.playLog;
    this.practicePlayLogCursor = absorbed.cursor;
  }

  private refreshPlayLog(): void {
    if (!this.playLogText) {
      return;
    }
    this.absorbPlayLogFromEncounter();
    const log = this.activePlayLog();
    if (log.length !== this.lastLogLength) {
      this.playLogScroll = 0;
      this.lastLogLength = log.length;
    }
    const visible = 14;
    const maxScroll = Math.max(0, log.length - visible);
    this.playLogScroll = Math.min(this.playLogScroll, maxScroll);
    const end = log.length - this.playLogScroll;
    const start = Math.max(0, end - visible);
    const slice = log.slice(start, end);
    this.playLogText.setText(
      slice.length > 0 ? slice.join('\n') : formatCombatLogPanel(log, visible),
    );
    if (this.playLogToggleLabel && !this.playLogOpen) {
      const n = log.length;
      this.playLogToggleLabel.setText(n > 0 ? `Log ▸${n}` : 'Log ▸');
    }
  }

  private renderEnemies(y: number): void {
    const enemies = this.state.enemies;
    const gap = Math.min(120, (GAME_WIDTH - 40) / Math.max(enemies.length, 1));
    const startX = GAME_WIDTH / 2 - ((enemies.length - 1) * gap) / 2;

    enemies.forEach((enemy, index) => {
      const x = startX + index * gap;
      const image = this.add
        .image(x, y + 36, 'px-enemy')
        .setInteractive({ useHandCursor: true })
        .setScale(enemies.length > 2 ? 0.75 : 0.9);
      image.on('pointerup', () => this.onSelectTarget(enemy.id));
      this.enemyImages.set(enemy.id, image);

      this.tweens.add({
        targets: image,
        y: image.y + 5,
        duration: 850 + index * 80,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });

      const label = this.add
        .text(x, y + 82, '', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '11px',
          color: '#e8d5a3',
          align: 'center',
        })
        .setOrigin(0.5);
      this.enemyLabels.set(enemy.id, label);

      const statusRoot = this.add.container(x, y + 98);
      this.enemyStatusRoots.set(enemy.id, statusRoot);

      const intent = this.add
        .text(x, y + 118, '', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '11px',
          color: '#c45c5c',
          align: 'center',
        })
        .setOrigin(0.5);
      this.enemyIntentLabels.set(enemy.id, intent);
    });
  }

  private refreshEnemyStatusRow(
    enemyId: string,
    statuses: EncounterState['enemies'][number]['statuses'],
    dead: boolean,
  ): void {
    const root = this.enemyStatusRoots.get(enemyId);
    if (!root) {
      return;
    }
    root.removeAll(true);
    if (dead || statuses.length === 0) {
      return;
    }
    const chipGap = 6;
    const built: { container: Phaser.GameObjects.Container; width: number }[] =
      [];
    for (const status of statuses) {
      const meta = ENEMY_STATUS_META[status.id];
      if (!meta) {
        continue;
      }
      const icon = addGameIcon(this, meta.icon, 0, 0, {
        size: 12,
        color: meta.tint,
        originX: 0,
        originY: 0.5,
      });
      const label = this.add
        .text(14, 0, `${meta.label}×${status.intensity}`, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '10px',
          color: meta.color,
        })
        .setOrigin(0, 0.5);
      const width = 14 + label.width;
      built.push({
        container: this.add.container(0, 0, [icon, label]),
        width,
      });
    }
    const total =
      built.reduce((sum, item) => sum + item.width, 0) +
      chipGap * Math.max(0, built.length - 1);
    let x = -total / 2;
    for (const item of built) {
      item.container.setX(x);
      root.add(item.container);
      x += item.width + chipGap;
    }
  }

  private shortSkillLabel(skillId: string | null, level: number): string {
    if (!skillId || level <= 0) {
      return '—';
    }
    const skill = getSkillDefinition(skillId);
    const short =
      skill.name.length > 8 ? `${skill.name.slice(0, 7)}…` : skill.name;
    return `${short} ${level}`;
  }

  private renderDiceTray(y: number): void {
    const unlocked = getUnlockedDice(this.state.tray);
    const locked = getLockedDice(this.state.tray);
    const totalSlotsHint = Math.min(
      MAX_DICE_SLOTS,
      unlocked.length + Math.min(locked.length, 3),
    );
    const visibleCount = Math.max(unlocked.length, Math.min(totalSlotsHint, 4));
    const slotGap = visibleCount > 3 ? 72 : 78;
    const startX =
      GAME_WIDTH / 2 - ((Math.min(MAX_DICE_SLOTS, unlocked.length + locked.length) - 1) * slotGap) / 2;

    this.state.tray.dice.forEach((die) => {
      // Only lay out first few locked hints to keep tray readable on phone.
      if (!die.unlocked && die.id > unlocked.length + 1) {
        return;
      }
      const x = startX + die.id * slotGap;
      if (die.unlocked) {
        const img = this.add
          .image(x, y, 'px-die')
          .setInteractive({ useHandCursor: true });
        img.on('pointerup', () => this.onDieTap(die.id));
        this.dieImages[die.id] = img;

        const faceText = this.add
          .text(x, y, '·', {
            fontFamily: 'Georgia, serif',
            fontSize: '22px',
            color: '#1a1408',
            fontStyle: 'bold',
          })
          .setOrigin(0.5);
        this.dieFaceTexts[die.id] = faceText;

        const holdHint = this.add
          .text(x, y + 26, '', {
            fontFamily: 'system-ui, sans-serif',
            fontSize: '9px',
            color: '#c9a227',
          })
          .setOrigin(0.5);
        this.dieHoldHints[die.id] = holdHint;

        const skillText = this.add
          .text(x, y + 40, '—', {
            fontFamily: 'system-ui, sans-serif',
            fontSize: '9px',
            color: '#9ec1ff',
            align: 'center',
          })
          .setOrigin(0.5);
        this.dieSkillTexts[die.id] = skillText;
      } else if (die.id < unlocked.length + 2) {
        const hint = this.add.image(x, y, 'px-die-locked').setAlpha(0.45);
        hint.setScale(0.7);
        this.add
          .text(x, y, '×', {
            fontFamily: 'system-ui, sans-serif',
            fontSize: '16px',
            color: '#6e7694',
          })
          .setOrigin(0.5)
          .setAlpha(0.7);
      }
    });
  }

  private makeButton(
    x: number,
    y: number,
    caption: string,
    handler: () => void,
    widthKey: 'px-btn' | 'px-btn-sm' | 'px-btn-xs' = 'px-btn-sm',
  ): UiButton {
    const bg = this.add.image(x, y, widthKey).setInteractive({ useHandCursor: true });
    const label = this.add
      .text(x, y, caption, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: widthKey === 'px-btn' ? '15px' : widthKey === 'px-btn-xs' ? '11px' : '12px',
        color: '#1a1408',
        fontStyle: 'bold',
        align: 'center',
      })
      .setOrigin(0.5);
    const button: UiButton = { bg, label, enabled: true, handler };
    bg.on('pointerup', () => {
      if (button.enabled) {
        handler();
      }
    });
    this.buttons.push(button);
    return button;
  }

  private renderControls(y: number): void {
    const cx = GAME_WIDTH / 2;
    // Row 1: full reroll · hold mode · attack. Tap a die to spend a single reroll.
    this.makeButton(cx - 110, y, 'Reroll All', () => this.onRerollAll(), 'px-btn-sm');
    this.makeButton(cx, y, 'Hold', () => this.onHoldMode(), 'px-btn-sm');
    this.makeButton(cx + 110, y, 'Attack', () => this.onAttack(), 'px-btn-sm');
    // Row 2: combo cycle centered
    this.makeButton(cx, y + 44, 'Cycle Combo', () => this.onCycleCombo(), 'px-btn');
  }

  private setButtonEnabled(button: UiButton, enabled: boolean): void {
    button.enabled = enabled;
    button.bg.setAlpha(enabled ? 1 : 0.35);
    button.label.setAlpha(enabled ? 1 : 0.45);
  }

  private refreshUi(): void {
    const { state } = this;
    const terminal = state.phase === 'won' || state.phase === 'lost';
    const leveling = state.phase === 'levelUp';

    const shield =
      state.player.shield > 0 ? ` · Shield ${state.player.shield}` : '';
    this.heroHpText.setText(
      `${state.player.name}  HP ${state.player.hp}/${state.player.maxHp}${shield}`,
    );
    this.xpText.setText(
      `Temp Lv ${state.progress.level} · XP ${state.progress.xp} · Offer rerolls ${state.progress.skillOfferRerollsRemaining}`,
    );

    for (const enemy of state.enemies) {
      const image = this.enemyImages.get(enemy.id);
      const label = this.enemyLabels.get(enemy.id);
      const intent = this.enemyIntentLabels.get(enemy.id);
      if (!image || !label || !intent) {
        continue;
      }
      const dead = enemy.hp <= 0;
      image.setAlpha(dead ? 0.25 : enemy.id === state.selectedTargetId ? 1 : 0.7);
      image.setTint(
        enemy.id === state.selectedTargetId && !dead ? 0xffffff : 0xbbbbbb,
      );
      if (enemy.id === state.selectedTargetId && !dead) {
        image.setScale(state.enemies.length > 2 ? 0.85 : 1);
      } else {
        image.setScale(state.enemies.length > 2 ? 0.7 : 0.85);
      }
      label.setText(
        dead
          ? `${enemy.name}  DOWN`
          : `${enemy.name}  HP ${enemy.hp}/${enemy.maxHp}`,
      );
      this.refreshEnemyStatusRow(enemy.id, enemy.statuses, dead);
      intent.setText(dead ? '—' : `Next: ${enemy.intention.label}`);
      intent.setColor(
        enemy.intention.kind === 'guard'
          ? '#7ec8a3'
          : enemy.intention.kind === 'special'
            ? '#e8a45c'
            : '#c45c5c',
      );
    }

    const selected = state.eligibleCombos.find(
      (c) => c.definition.id === state.selectedComboId,
    );
    const skillReadyIds = new Set(
      (selected?.participatingDieIds ?? []).filter((id) => {
        const die = state.tray.dice[id];
        return Boolean(die?.primarySkillId && die.primarySkillLevel > 0);
      }),
    );

    for (const die of getUnlockedDice(state.tray)) {
      const face = this.dieFaceTexts[die.id];
      const hold = this.dieHoldHints[die.id];
      const skill = this.dieSkillTexts[die.id];
      const img = this.dieImages[die.id];
      if (face) {
        face.setText(die.face?.toString() ?? '·');
      }
      if (hold) {
        hold.setText(die.held ? 'HOLD' : '');
      }
      const willFire = skillReadyIds.has(die.id);
      if (skill) {
        skill.setText(this.shortSkillLabel(die.primarySkillId, die.primarySkillLevel));
        skill.setColor(willFire ? '#f2e6c8' : die.primarySkillId ? '#9ec1ff' : '#4a5168');
      }
      if (img) {
        if (willFire) {
          img.setTint(0x7ed0ff);
          this.ensureSkillGlow(die.id, img);
        } else {
          this.clearSkillGlow(die.id);
          img.setTint(die.held ? 0xffe08a : 0xffffff);
          img.setScale(1);
        }
      }
    }

    this.comboText.setText(
      selected
        ? `${selected.definition.name} ×${selected.definition.multiplier} (${selected.estimatedDamage} dmg)`
        : state.hasRolledThisTurn
          ? 'No combo'
          : 'Waiting…',
    );

    this.statusText.setText(this.statusMessage());
    this.refreshPlayLog();

    const canAct =
      !this.actionLocked &&
      !terminal &&
      !leveling &&
      state.hasRolledThisTurn &&
      (state.phase === 'comboSelection' || state.phase === 'rolling');
    const canReroll = canAct;
    const canAttack =
      canAct &&
      state.selectedComboId !== null &&
      state.selectedTargetId !== null;

    // buttons: 0 Reroll All, 1 Hold, 2 Attack, 3 Cycle Combo
    if (this.buttons[0]) {
      this.setButtonEnabled(
        this.buttons[0],
        canReroll && state.rerollAllRemaining > 0,
      );
      this.buttons[0].label.setText(`All (${state.rerollAllRemaining})`);
    }
    if (this.buttons[1]) {
      this.setButtonEnabled(this.buttons[1], canAct);
      this.buttons[1].label.setText(this.holdMode ? 'Holding…' : 'Hold');
      this.buttons[1].bg.setTint(this.holdMode ? 0xffe08a : 0xffffff);
    }
    if (this.buttons[2]) {
      this.setButtonEnabled(this.buttons[2], canAttack);
    }
    if (this.buttons[3]) {
      this.setButtonEnabled(
        this.buttons[3],
        canAttack && state.eligibleCombos.length > 1,
      );
    }

    if (leveling && !this.holdResultOverlays) {
      this.showLevelUpOverlay();
    } else if (!leveling && this.levelUpOverlay) {
      this.levelUpOverlay.destroy(true);
      this.levelUpOverlay = undefined;
    }

    if (this.progressText && this.dungeonRun) {
      const boss = isBossEncounter(this.dungeonRun) ? ' · BOSS' : '';
      this.progressText.setText(
        `${encounterProgressLabel(this.dungeonRun)}${boss}`,
      );
    }

    if (terminal && !leveling && !this.holdResultOverlays) {
      this.handleTerminalPhase();
    }
  }

  private persistDungeon(options: { bankCombatXp?: boolean } = {}): void {
    if (this.mode !== 'dungeon' || !this.dungeonRun) {
      return;
    }
    this.dungeonRun = syncDungeonEncounter(this.dungeonRun, this.state);
    const previous = this.saveStore.load();
    let game = updateSavedGameRun(previous, this.dungeonRun, this.rng.state);

    // Only bank XP when explicitly requested after a combat resolution tick.
    // Re-persisting the same lastEvents (e.g. leaving to Village) must not
    // double-count provisional Class XP.
    if (options.bankCombatXp && game.run && game.run.status === 'active') {
      const gained = this.state.lastEvents
        .filter((e) => e.type === 'xp_gained')
        .reduce((sum, e) => sum + (e.type === 'xp_gained' ? e.amount : 0), 0);
      if (gained > 0) {
        game = {
          ...game,
          run: accumulateProvisionalClassXp(game.run, gained),
        };
      }
    }

    this.saveStore.save(game);
  }

  private handleTerminalPhase(): void {
    if (this.mode === 'dungeon' && this.dungeonRun) {
      if (this.state.phase === 'won') {
        if (this.rewardOverlay || this.dungeonRun.phase === 'reward') {
          if (!this.rewardOverlay && this.dungeonRun.phase === 'reward') {
            this.showRewardOverlay();
          }
          return;
        }
        if (this.state.progress.pendingLevelUps > 0) {
          return;
        }
        this.dungeonRun = syncDungeonEncounter(this.dungeonRun, this.state);
        this.dungeonRun = enterRewardPhase(this.dungeonRun, this.rng);
        this.persistDungeon();
        this.showRewardOverlay();
        return;
      }
      if (this.state.phase === 'lost' && !this.endOverlay) {
        this.dungeonRun = syncDungeonEncounter(this.dungeonRun, this.state);
        this.dungeonRun = enterDefeatResults(this.dungeonRun);
        this.persistDungeon();
        this.scene.start('DungeonResult', { dungeonRun: this.dungeonRun });
      }
      return;
    }

    if (!this.endOverlay) {
      this.showEndOverlay();
    }
  }

  private ensureSkillGlow(dieId: number, img: Phaser.GameObjects.Image): void {
    if (this.skillGlowTweens.has(dieId)) {
      return;
    }
    const tween = this.tweens.add({
      targets: img,
      scaleX: 1.12,
      scaleY: 1.12,
      duration: 420,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.skillGlowTweens.set(dieId, tween);
  }

  private clearSkillGlow(dieId: number): void {
    const tween = this.skillGlowTweens.get(dieId);
    if (tween) {
      tween.stop();
      this.skillGlowTweens.delete(dieId);
    }
  }

  private statusMessage(): string {
    const unlocked = getUnlockedDice(this.state.tray);
    const faces = unlocked
      .map((d) => (d.face === undefined ? '—' : String(d.face)))
      .join(', ');
    if (this.pendingSkillDieId) {
      return 'Choose which die gets the skill';
    }
    if (this.holdMode) {
      return `Hold mode · tap a die · singles left ${this.state.rerollSingleRemaining}`;
    }
    return `Turn ${this.state.turn} · tap die to reroll (${this.state.rerollSingleRemaining}) · [${faces}]`;
  }

  private showFeedback(message: string, color = '#f2e6c8'): void {
    this.feedbackText.setText(message);
    this.feedbackText.setColor(color);
    this.feedbackText.setAlpha(1);
    this.feedbackText.y = GAME_HEIGHT * 0.44 - 96;
    this.tweens.killTweensOf(this.feedbackText);
    this.tweens.add({
      targets: this.feedbackText,
      alpha: 0,
      y: this.feedbackText.y - 14,
      duration: 1600,
      ease: 'Quad.easeOut',
    });
  }

  private animateAttack(splashEnemyIds: string[] = []): void {
    const heroBaseY = this.hero.y;
    this.tweens.add({
      targets: this.hero,
      y: heroBaseY - 28,
      duration: 140,
      yoyo: true,
      ease: 'Quad.easeOut',
      onComplete: () => {
        this.hero.setY(heroBaseY);
      },
    });
    const targetId = this.state.selectedTargetId;
    const hitIds = new Set<string>([
      ...(targetId ? [targetId] : []),
      ...splashEnemyIds,
    ]);
    for (const id of hitIds) {
      const img = this.enemyImages.get(id);
      if (!img) {
        continue;
      }
      const baseX = img.x;
      this.tweens.add({
        targets: img,
        alpha: 0.35,
        x: baseX + (id === targetId ? 10 : 6),
        duration: 80,
        yoyo: true,
        repeat: id === targetId ? 2 : 1,
        onComplete: () => {
          img.setX(baseX);
        },
      });
    }
  }

  private showDamageFloat(enemyId: string, amount: number): void {
    if (amount <= 0) {
      return;
    }
    const img = this.enemyImages.get(enemyId);
    if (!img) {
      return;
    }
    const label = this.add
      .text(img.x, img.y - 18, `-${amount}`, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#f2e6c8',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(20);
    this.tweens.add({
      targets: label,
      y: label.y - 30,
      alpha: 0,
      duration: 750,
      ease: 'Quad.easeOut',
      onComplete: () => label.destroy(),
    });
  }

  private animateEnemyAttack(enemyId: string, onComplete: () => void): void {
    const img = this.enemyImages.get(enemyId);
    if (!img) {
      onComplete();
      return;
    }
    // Use X lunge so it doesn't fight the idle Y bob tween.
    const baseX = img.x;
    const baseScaleX = img.scaleX;
    const baseScaleY = img.scaleY;
    this.tweens.add({
      targets: img,
      x: baseX + (baseX < GAME_WIDTH / 2 ? 16 : -16),
      scaleX: baseScaleX * 1.12,
      scaleY: baseScaleY * 1.12,
      duration: 150,
      yoyo: true,
      ease: 'Quad.easeIn',
      onComplete: () => {
        img.setX(baseX);
        img.setScale(baseScaleX, baseScaleY);
        this.tweens.add({
          targets: this.hero,
          alpha: 0.4,
          duration: 70,
          yoyo: true,
          repeat: 1,
          onComplete,
        });
      },
    });
  }

  private flashDieSkill(dieId: number): void {
    const img = this.dieImages[dieId];
    const skill = this.dieSkillTexts[dieId];
    if (img) {
      this.tweens.add({
        targets: img,
        scaleX: 1.15,
        scaleY: 1.15,
        duration: 120,
        yoyo: true,
      });
    }
    if (skill) {
      skill.setColor('#f2e6c8');
      this.time.delayedCall(500, () => {
        if (skill.active) {
          skill.setColor('#9ec1ff');
        }
      });
    }
  }

  private onSelectTarget(enemyId: string): void {
    if (
      this.actionLocked ||
      this.state.phase === 'won' ||
      this.state.phase === 'lost' ||
      this.state.phase === 'levelUp'
    ) {
      return;
    }
    const enemy = this.state.enemies.find((e) => e.id === enemyId);
    if (!enemy || enemy.hp <= 0) {
      return;
    }
    this.state = selectTarget(this.state, enemyId);
    this.showFeedback(`Target: ${enemy.name}`);
    this.refreshUi();
  }

  private spinDie(dieId: number, onComplete?: () => void): void {
    const img = this.dieImages[dieId];
    const face = this.dieFaceTexts[dieId];
    const targets = [img, face].filter(
      (obj): obj is Phaser.GameObjects.Image | Phaser.GameObjects.Text =>
        Boolean(obj),
    );
    if (targets.length === 0) {
      onComplete?.();
      return;
    }
    for (const target of targets) {
      this.tweens.killTweensOf(target);
      target.setAngle(0);
    }
    this.tweens.add({
      targets,
      angle: 360,
      duration: 320,
      ease: 'Cubic.easeOut',
      onComplete: () => {
        for (const target of targets) {
          target.setAngle(0);
        }
        onComplete?.();
      },
    });
  }

  private onDieTap(dieId: number): void {
    if (this.state.phase === 'won' || this.state.phase === 'lost') {
      return;
    }
    if (this.state.phase === 'levelUp' && this.pendingSkillDieId) {
      try {
        this.state = chooseSkillFromOffer(
          this.state,
          this.pendingSkillDieId,
          this.rng,
          dieId,
        );
        this.pendingSkillDieId = null;
        this.showFeedback(`Skill on die ${dieId + 1}`, '#9ec1ff');
        this.refreshUi();
      } catch (error) {
        this.showFeedback(error instanceof Error ? error.message : 'Place failed');
      }
      return;
    }
    if (this.state.phase === 'levelUp') {
      return;
    }
    if (!this.state.hasRolledThisTurn) {
      return;
    }

    if (this.holdMode) {
      try {
        this.state = holdDie(this.state, dieId);
        const die = this.state.tray.dice[dieId];
        this.showFeedback(die?.held ? 'Held' : 'Released', '#c9a227');
        this.refreshUi();
      } catch {
        // ignore invalid hold taps
      }
      return;
    }

    if (this.state.rerollSingleRemaining <= 0) {
      this.showFeedback('No single rerolls left — use Hold to lock dice', '#c45c5c');
      return;
    }
    if (
      this.state.phase !== 'comboSelection' &&
      this.state.phase !== 'rolling'
    ) {
      return;
    }

    try {
      this.spinDie(dieId, () => {
        // Face text already refreshed after state update below; spin is visual only.
      });
      this.state = combatRerollSingle(this.state, dieId, this.rng);
      this.showFeedback(
        `Rerolled die ${dieId + 1} (${this.state.rerollSingleRemaining} left)`,
        '#c9a227',
      );
      this.refreshUi();
    } catch (error) {
      this.showFeedback(error instanceof Error ? error.message : 'Reroll failed');
    }
  }

  private onRerollAll(): void {
    try {
      const unlockedIds = getUnlockedDice(this.state.tray)
        .filter((d) => !d.held)
        .map((d) => d.id);
      for (const id of unlockedIds) {
        this.spinDie(id);
      }
      this.state = combatRerollAll(this.state, this.rng);
      this.showFeedback('Full reroll', '#c9a227');
      this.refreshUi();
    } catch (error) {
      this.showFeedback(error instanceof Error ? error.message : 'Cannot reroll');
    }
  }

  private onHoldMode(): void {
    if (
      this.state.phase === 'won' ||
      this.state.phase === 'lost' ||
      this.state.phase === 'levelUp' ||
      !this.state.hasRolledThisTurn
    ) {
      return;
    }
    this.holdMode = !this.holdMode;
    this.showFeedback(
      this.holdMode ? 'Hold mode — tap dice to lock' : 'Tap dice to reroll',
      '#c9a227',
    );
    this.refreshUi();
  }

  private onCycleCombo(): void {
    if (this.state.eligibleCombos.length < 2 || !this.state.selectedComboId) {
      return;
    }
    const ids = this.state.eligibleCombos.map((c) => c.definition.id);
    const idx = ids.indexOf(this.state.selectedComboId);
    const next = ids[(idx + 1) % ids.length] as ComboId;
    this.state = selectCombo(this.state, next);
    const selected = this.state.eligibleCombos.find(
      (c) => c.definition.id === next,
    );
    this.showFeedback(selected?.definition.name ?? next, '#e8d5a3');
    this.refreshUi();
  }

  private onAttack(): void {
    if (this.actionLocked) {
      return;
    }
    try {
      const enemiesBefore = this.state.enemies.map((e) => ({
        id: e.id,
        name: e.name,
        hp: e.hp,
      }));
      // Resolve player attack first so skill events are still on lastEvents
      // (commitPlayerAttack → enemy turn → auto-roll clears lastEvents).
      const afterPlayer = resolvePlayerAttack(this.state, this.rng);

      const attackEvent = afterPlayer.lastEvents.find(
        (e) => e.type === 'player_attack',
      );
      const skillEvents = afterPlayer.lastEvents.filter(
        (e) => e.type === 'skill_activated',
      );
      const primaryTargetId =
        (attackEvent && attackEvent.type === 'player_attack'
          ? attackEvent.targetId
          : null) ?? this.state.selectedTargetId;
      const splashIds = afterPlayer.enemies
        .filter((e) => {
          const before = enemiesBefore.find((b) => b.id === e.id);
          if (!before || e.id === primaryTargetId) {
            return false;
          }
          return e.hp < before.hp;
        })
        .map((e) => e.id);

      this.actionLocked = true;
      this.holdResultOverlays = true;
      this.animateAttack(splashIds);

      skillEvents.forEach((event) => {
        if (event.type === 'skill_activated') {
          this.flashDieSkill(event.dieId);
        }
      });

      for (const before of enemiesBefore) {
        const after = afterPlayer.enemies.find((e) => e.id === before.id);
        if (!after) {
          continue;
        }
        const dealt = before.hp - after.hp;
        if (dealt > 0) {
          this.showDamageFloat(before.id, dealt);
        }
      }

      const skillNames = skillEvents
        .map((e) => (e.type === 'skill_activated' ? e.skillName : ''))
        .filter(Boolean);
      const skillHints = skillEvents
        .map((e) => {
          if (e.type !== 'skill_activated') return '';
          if (e.skillId === 'cleave') return 'AoE';
          if (e.skillId === 'rend') return 'Bleed';
          if (e.statusApplied === 'vulnerable') return 'Vulnerable';
          if (e.skillId === 'bloodlust') return 'Heal';
          if (e.damage && e.damage > 0) return `+${e.damage}`;
          return '';
        })
        .filter(Boolean);
      const base =
        attackEvent && attackEvent.type === 'player_attack'
          ? `${attackEvent.comboName} → ${attackEvent.damage} dmg`
          : 'Attack';
      const detail =
        skillNames.length > 0
          ? `${base} · ${skillNames.join(' + ')}${
              skillHints.length ? ` (${skillHints.join(', ')})` : ''
            }`
          : base;
      this.showFeedback(detail, skillNames.length > 0 ? '#9ec1ff' : '#f2e6c8');
      this.activationText.setText(
        skillNames.length > 0 ? skillNames.join(' · ') : base,
      );

      // Show HP / status changes while holding end / level-up overlays.
      this.state = afterPlayer;
      this.refreshUi();

      const defeated = enemiesBefore.filter((before) => {
        const after = afterPlayer.enemies.find((e) => e.id === before.id);
        return before.hp > 0 && after && after.hp <= 0;
      });
      const presentMs =
        720 + defeated.length * 280 + (splashIds.length > 0 ? 180 : 0);

      this.time.delayedCall(Math.min(presentMs, 1400), () => {
        if (defeated.length > 0) {
          this.showFeedback(
            defeated.map((e) => `${e.name} defeated!`).join(' · '),
            '#7ec8a3',
          );
        }

        const finishPlayerPresentation = (): void => {
          this.holdResultOverlays = false;
          if (
            afterPlayer.phase === 'won' ||
            afterPlayer.phase === 'lost' ||
            afterPlayer.phase === 'levelUp'
          ) {
            this.actionLocked = false;
            this.persistDungeon({ bankCombatXp: true });
            this.refreshUi();
            return;
          }

          if (afterPlayer.phase !== 'enemyActions') {
            this.actionLocked = false;
            this.persistDungeon({ bankCombatXp: true });
            this.refreshUi();
            return;
          }

          const beforeEnemy = afterPlayer;
          const afterEnemy = resolveEnemyActions(beforeEnemy, this.rng);
          this.playEnemyActionsSequence(beforeEnemy, afterEnemy, () => {
            this.state = afterEnemy;
            this.holdResultOverlays = false;
            this.actionLocked = false;
            this.persistDungeon({ bankCombatXp: true });
            this.refreshUi();
          });
        };

        // Brief beat after defeat callouts before overlays / enemy turn.
        this.time.delayedCall(defeated.length > 0 ? 420 : 80, finishPlayerPresentation);
      });
    } catch (error) {
      this.actionLocked = false;
      this.holdResultOverlays = false;
      this.showFeedback(error instanceof Error ? error.message : 'Cannot attack');
    }
  }

  /**
   * Plays bleed ticks / enemy strikes one-after-another so the enemy turn is
   * readable, then applies the fully resolved encounter state.
   */
  private playEnemyActionsSequence(
    before: EncounterState,
    after: EncounterState,
    onDone: () => void,
  ): void {
    const newEvents = after.lastEvents.slice(before.lastEvents.length);
    const steps = newEvents.filter(
      (e) =>
        e.type === 'status_tick' ||
        e.type === 'enemy_attack' ||
        e.type === 'enemy_stunned',
    );

    if (steps.length === 0) {
      this.time.delayedCall(280, onDone);
      return;
    }

    let stepIndex = 0;
    const playStep = (): void => {
      if (stepIndex >= steps.length) {
        this.time.delayedCall(200, onDone);
        return;
      }
      const event = steps[stepIndex]!;
      stepIndex += 1;

      if (event.type === 'status_tick') {
        const enemy = before.enemies.find((e) => e.id === event.enemyId);
        const name = enemy?.name ?? 'Enemy';
        this.showFeedback(`${name} bleeds for ${event.damage}`, '#e07070');
        const img = this.enemyImages.get(event.enemyId);
        if (img) {
          this.tweens.add({
            targets: img,
            alpha: 0.35,
            duration: 80,
            yoyo: true,
            repeat: 1,
          });
        }
        const label = this.enemyLabels.get(event.enemyId);
        if (label && enemy) {
          label.setText(
            `${enemy.name}  HP ${event.targetHpAfter}/${enemy.maxHp}`,
          );
        }
        this.time.delayedCall(480, playStep);
        return;
      }

      if (event.type === 'enemy_stunned') {
        const enemy = before.enemies.find((e) => e.id === event.enemyId);
        this.showFeedback(
          `${enemy?.name ?? 'Enemy'} is stunned`,
          '#e8c45c',
        );
        this.time.delayedCall(420, playStep);
        return;
      }

      if (event.type === 'enemy_attack') {
        const enemy = before.enemies.find((e) => e.id === event.enemyId);
        const name = enemy?.name ?? 'Enemy';
        this.animateEnemyAttack(event.enemyId, () => {
          if (event.dodged) {
            this.showFeedback(`${name} attack DODGED`, '#7ec8a3');
          } else if (event.blocked > 0 && event.damage === 0) {
            this.showFeedback(`${name} attack BLOCKED`, '#7ec8a3');
          } else {
            this.showFeedback(
              `${name} hits for ${event.damage}`,
              '#c45c5c',
            );
          }
          this.heroHpText.setText(
            `${before.player.name}  HP ${event.playerHpAfter}/${before.player.maxHp}`,
          );
          this.time.delayedCall(520, playStep);
        });
        return;
      }

      this.time.delayedCall(200, playStep);
    };

    this.showFeedback('Enemy turn…', '#c9a227');
    this.time.delayedCall(380, playStep);
  }

  private onDevUnlockDie(): void {
    if (this.state.phase === 'levelUp') {
      return;
    }
    const next = Math.min(MAX_DICE_SLOTS, this.state.tray.unlockedCount + 1);
    if (next === this.state.tray.unlockedCount) {
      this.showFeedback('All 6 dice unlocked');
      return;
    }
    // Recreate scene visuals for new dice by restarting with preserved seed state is hard;
    // instead mutate and soft-restart the combat scene with current unlock count + same seed branch.
    this.scene.restart({
      unlockedDiceSlots: next,
      seed: this.rng.state,
      enemyTemplateIds: this.state.enemies.map((e) => e.templateId),
    });
  }

  private showLevelUpOverlay(): void {
    if (this.levelUpOverlay) {
      this.levelUpOverlay.destroy(true);
      this.levelUpOverlay = undefined;
    }
    const offers = this.state.progress.currentOffer;
    if (!offers) {
      return;
    }

    const container = this.add.container(0, 0);
    const veil = this.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.78)
      .setOrigin(0);

    const title = this.add
      .text(
        GAME_WIDTH / 2,
        90,
        this.state.progress.offerKind === 'first_primary'
          ? 'LEVEL UP — Primary Skill'
          : 'LEVEL UP — Choose Skill',
        {
          fontFamily: 'Georgia, serif',
          fontSize: '22px',
          color: '#e8d5a3',
          align: 'center',
        },
      )
      .setOrigin(0.5);

    const subtitle = this.add
      .text(
        GAME_WIDTH / 2,
        122,
        `Temp Lv ${this.state.progress.level} · Offer rerolls ${this.state.progress.skillOfferRerollsRemaining}`,
        {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '12px',
          color: '#9aa3c0',
        },
      )
      .setOrigin(0.5);

    const nodes: Phaser.GameObjects.GameObject[] = [veil, title, subtitle];

    offers.forEach((offer, index) => {
      const isEnh = offer.kind === 'enhancement';
      const skill = isEnh ? null : getSkillDefinition(offer.skillId);
      const title =
        offer.displayName ?? skill?.name ?? offer.offerId;
      const body =
        offer.displayDescription ?? skill?.description ?? '';
      const y = 180 + index * 118;
      const card = this.add
        .rectangle(GAME_WIDTH / 2, y, GAME_WIDTH - 40, 104, 0x1c2438)
        .setStrokeStyle(1, isEnh ? 0x7ec8a3 : 0xc9a227)
        .setInteractive({ useHandCursor: true });
      const name = this.add
        .text(GAME_WIDTH / 2, y - 32, title, {
          fontFamily: 'Georgia, serif',
          fontSize: '18px',
          color: '#f2e6c8',
        })
        .setOrigin(0.5);
      const meta = this.add
        .text(
          GAME_WIDTH / 2,
          y - 8,
          isEnh
            ? `Enhancement · ${offer.exclusionNote ?? 'Single choice'}`
            : offer.isUpgrade
              ? `Upgrade Lv${offer.currentLevel} → ${offer.nextLevel} · ${skill!.category}`
              : `New · Lv1 · ${skill!.category}`,
          {
            fontFamily: 'system-ui, sans-serif',
            fontSize: '11px',
            color: isEnh ? '#7ec8a3' : '#c9a227',
          },
        )
        .setOrigin(0.5);
      const desc = this.add
        .text(GAME_WIDTH / 2, y + 22, body, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '12px',
          color: '#9aa3c0',
          align: 'center',
          wordWrap: { width: GAME_WIDTH - 64 },
        })
        .setOrigin(0.5);
      card.on('pointerup', () => this.onPickSkill(offer.offerId));
      nodes.push(card, name, meta, desc);
    });

    const rerollBtn = this.add
      .image(GAME_WIDTH / 2, GAME_HEIGHT - 70, 'px-btn')
      .setInteractive({ useHandCursor: true });
    const rerollLabel = this.add
      .text(
        GAME_WIDTH / 2,
        GAME_HEIGHT - 70,
        `Reroll Offers (${this.state.progress.skillOfferRerollsRemaining})`,
        {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '14px',
          color: '#1a1408',
          fontStyle: 'bold',
        },
      )
      .setOrigin(0.5);
    const canReroll = this.state.progress.skillOfferRerollsRemaining > 0;
    rerollBtn.setAlpha(canReroll ? 1 : 0.35);
    rerollLabel.setAlpha(canReroll ? 1 : 0.45);
    if (canReroll) {
      rerollBtn.on('pointerup', () => this.onRerollOffers());
    }
    nodes.push(rerollBtn, rerollLabel);

    if (this.pendingSkillDieId) {
      const pending = getSkillDefinition(this.pendingSkillDieId);
      const hint = this.add
        .text(
          GAME_WIDTH / 2,
          GAME_HEIGHT - 170,
          `Place ${pending.name} — tap a die`,
          {
            fontFamily: 'system-ui, sans-serif',
            fontSize: '13px',
            color: '#f2e6c8',
          },
        )
        .setOrigin(0.5);
      nodes.push(hint);
      const unlocked = getUnlockedDice(this.state.tray);
      const gap = Math.min(90, (GAME_WIDTH - 48) / Math.max(unlocked.length, 1));
      const startX = GAME_WIDTH / 2 - ((unlocked.length - 1) * gap) / 2;
      unlocked.forEach((die, index) => {
        const x = startX + index * gap;
        const occupied = die.primarySkillId !== null;
        const chip = this.add
          .rectangle(x, GAME_HEIGHT - 120, 78, 40, occupied ? 0xb07a3a : 0xc9a227)
          .setInteractive({ useHandCursor: true });
        const chipLabel = this.add
          .text(
            x,
            GAME_HEIGHT - 120,
            occupied ? `Die ${die.id + 1}\nreplace` : `Die ${die.id + 1}`,
            {
              fontFamily: 'system-ui, sans-serif',
              fontSize: '11px',
              color: '#1a1408',
              fontStyle: 'bold',
              align: 'center',
            },
          )
          .setOrigin(0.5);
        chip.on('pointerup', () => {
          const skillId = this.pendingSkillDieId;
          if (!skillId) {
            return;
          }
          try {
            this.state = chooseSkillFromOffer(this.state, skillId, this.rng, die.id);
            this.pendingSkillDieId = null;
            this.persistDungeon();
            this.showFeedback(
              occupied
                ? `Replaced die ${die.id + 1}`
                : `Equipped on die ${die.id + 1}`,
              '#9ec1ff',
            );
            this.refreshUi();
          } catch (error) {
            this.showFeedback(
              error instanceof Error ? error.message : 'Place failed',
            );
          }
        });
        nodes.push(chip, chipLabel);
      });
    }

    container.add(nodes);
    container.setDepth(1000);
    this.levelUpOverlay = container;
  }

  private onPickSkill(skillId: string): void {
    if (skillChoiceNeedsDie(this.state, skillId)) {
      this.pendingSkillDieId = skillId;
      this.showFeedback('Choose which die gets this skill', '#c9a227');
      this.refreshUi();
      return;
    }
    try {
      const offer = this.state.progress.currentOffer?.find(
        (o) => o.offerId === skillId || o.skillId === skillId,
      );
      this.state = chooseSkillFromOffer(this.state, skillId, this.rng);
      this.pendingSkillDieId = null;
      this.persistDungeon();
      if (offer?.kind === 'enhancement') {
        this.showFeedback(
          `Enhancement: ${offer.displayName ?? skillId}`,
          '#7ec8a3',
        );
      } else {
        const skill = getSkillDefinition(offer?.skillId ?? skillId);
        this.showFeedback(`Chose ${skill.name}`, '#9ec1ff');
      }
      this.refreshUi();
    } catch (error) {
      this.showFeedback(error instanceof Error ? error.message : 'Cannot pick skill');
    }
  }

  private onRerollOffers(): void {
    try {
      this.state = combatRerollSkillOffers(this.state, this.rng);
      this.pendingSkillDieId = null;
      this.persistDungeon();
      this.showFeedback('Offers rerolled', '#c9a227');
      this.refreshUi();
    } catch (error) {
      this.showFeedback(error instanceof Error ? error.message : 'Cannot reroll offers');
    }
  }

  private showRewardOverlay(): void {
    if (this.rewardOverlay || !this.dungeonRun?.pendingReward) {
      return;
    }
    const reward = this.dungeonRun.pendingReward;
    const container = this.add.container(0, 0);
    const veil = this.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.75)
      .setOrigin(0);
    const title = this.add
      .text(
        GAME_WIDTH / 2,
        GAME_HEIGHT * 0.28,
        reward.isBoss ? 'BOSS REWARD' : 'REWARD',
        {
          fontFamily: 'Georgia, serif',
          fontSize: '34px',
          color: '#e8d5a3',
        },
      )
      .setOrigin(0.5);

    const progress = this.add
      .text(
        GAME_WIDTH / 2,
        GAME_HEIGHT * 0.36,
        encounterProgressLabel(this.dungeonRun),
        {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '14px',
          color: '#c9a227',
        },
      )
      .setOrigin(0.5);

    const lines = [
      `+${reward.gold} gold (provisional)`,
      `+${reward.materials} materials (provisional)`,
    ];
    const gear = reward.equipment ?? [];
    if (reward.bossChest) {
      lines.push(
        gear.some((g) => g.lootSource === 'boss_chest')
          ? `Boss chest: ${gear.filter((g) => g.lootSource === 'boss_chest').length} item(s)`
          : 'Boss chest',
      );
    }
    if (gear.length > 0) {
      lines.push(`Equipment: ${gear.length} provisional`);
      for (const item of gear.slice(0, 4)) {
        lines.push(`· ${describeLootItem(item)}`);
      }
      if (gear.length > 4) {
        lines.push(`· +${gear.length - 4} more`);
      }
    }
    lines.push(
      `HP ${this.state.player.hp}/${this.state.player.maxHp} · Temp Lv ${this.state.progress.level}`,
    );
    lines.push('No free heal between ordinary encounters');

    const detail = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT * 0.48, lines.join('\n'), {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        color: '#9aa3c0',
        align: 'center',
        lineSpacing: 8,
        wordWrap: { width: GAME_WIDTH - 48 },
      })
      .setOrigin(0.5);

    const btn = this.add
      .image(GAME_WIDTH / 2, GAME_HEIGHT * 0.68, 'px-btn')
      .setInteractive({ useHandCursor: true });
    const btnLabel = this.add
      .text(
        GAME_WIDTH / 2,
        GAME_HEIGHT * 0.68,
        reward.isBoss ? 'Open Chest' : 'Collect & Continue',
        {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '15px',
          color: '#1a1408',
          fontStyle: 'bold',
        },
      )
      .setOrigin(0.5);
    btn.on('pointerup', () => this.onCollectReward());
    container.add([veil, title, progress, detail, btn, btnLabel]);
    container.setDepth(950);
    this.rewardOverlay = container;
  }

  private onCollectReward(): void {
    if (!this.dungeonRun) {
      return;
    }
    try {
      const pendingGear = [
        ...(this.dungeonRun.pendingReward?.equipment ?? []),
      ] as GeneratedLootItem[];
      const next = collectRewardAndAdvance(this.dungeonRun, this.rng);
      this.dungeonRun = next;
      const previous = this.saveStore.load();
      let game = updateSavedGameRun(previous, next, this.rng.state);

      // Bank provisional equipment into run state (victory settlement later).
      if (pendingGear.length > 0 && game.run && game.run.status === 'active') {
        const now = Date.now();
        game = {
          ...game,
          run: addProvisionalLoot(
            game.run,
            pendingGear.map((g) => ({
              instanceId: g.instanceId,
              defId: g.defId,
              rarity: g.rarity,
              source: g.lootSource,
              affixes: g.affixes,
              itemLevel: g.itemLevel,
              // stamp acquire time when collected mid-run
              name: undefined,
            })),
          ),
        };
        // Attach acquiredAt for display consistency once banked permanently.
        void now;
      }

      this.saveStore.save(game);
      this.showLootToasts(pendingGear);

      if (next.phase === 'results') {
        this.scene.start('DungeonResult', { dungeonRun: next });
        return;
      }

      // Auto-advance into the next encounter (fresh auto-roll inside domain).
      this.scene.restart({
        mode: 'dungeon',
        dungeonRun: next,
        rngState: this.rng.state,
      });
    } catch (error) {
      this.showFeedback(
        error instanceof Error ? error.message : 'Cannot collect reward',
      );
    }
  }

  /** Compact mid-run loot notifications — do not block dice/combat. */
  private showLootToasts(items: GeneratedLootItem[]): void {
    if (items.length === 0) return;
    const startY = 72;
    items.slice(0, 3).forEach((item, i) => {
      const def = getItemDef(item.defId);
      const color = RARITY_DISPLAY_COLORS[String(item.rarity)] ?? '#f2e6c8';
      const y = startY + i * 36;
      const bg = this.add
        .rectangle(GAME_WIDTH / 2, y, GAME_WIDTH - 24, 32, 0x121820, 0.88)
        .setStrokeStyle(1, 0x3a4558)
        .setDepth(1200);
      const icon = addItemIcon(this, def, item.rarity, 28, y, {
        size: 18,
        fallbackColor: Phaser.Display.Color.HexStringToColor(color).color,
        instanceId: item.instanceId,
      });
      icon.setDepth(1201);
      const label = this.add
        .text(46, y, describeLootItem(item), {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '12px',
          color,
        })
        .setOrigin(0, 0.5)
        .setDepth(1201);
      this.tweens.add({
        targets: [bg, icon, label],
        alpha: 0,
        y: '-=12',
        delay: 1600 + i * 200,
        duration: 500,
        onComplete: () => {
          bg.destroy();
          icon.destroy();
          label.destroy();
        },
      });
    });
    if (items.length > 3) {
      this.showFeedback(`+${items.length - 3} more loot`, '#c9a227');
    }
  }

  private showEndOverlay(): void {
    const won = this.state.phase === 'won';
    const container = this.add.container(0, 0);
    const veil = this.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.72)
      .setOrigin(0);
    const title = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT * 0.36, won ? 'VICTORY' : 'DEFEAT', {
        fontFamily: 'Georgia, serif',
        fontSize: '40px',
        color: won ? '#e8d5a3' : '#c45c5c',
      })
      .setOrigin(0.5);
    const detail = this.add
      .text(
        GAME_WIDTH / 2,
        GAME_HEIGHT * 0.44,
        won
          ? `All enemies defeated · Temp Lv ${this.state.progress.level}`
          : 'Temporary XP and skills are lost between runs',
        {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '14px',
          color: '#9aa3c0',
          align: 'center',
          wordWrap: { width: GAME_WIDTH - 48 },
        },
      )
      .setOrigin(0.5);
    const btn = this.add
      .image(GAME_WIDTH / 2, GAME_HEIGHT * 0.56, 'px-btn')
      .setInteractive({ useHandCursor: true });
    const btnLabel = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT * 0.56, 'Debug Menu', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#1a1408',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    btn.on('pointerup', () => this.scene.start('DebugMenu'));
    container.add([veil, title, detail, btn, btnLabel]);
    container.setDepth(900);
    this.endOverlay = container;
  }
}
